import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ApiService from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Target, Sparkles } from 'lucide-react';
import ForecastProgressModal from '../components/ForecastProgressModal';
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Button,
  Select,
  Alert,
  Badge,
  StatCard,
  StatGrid
} from '../components/ui';

const SectionCard = styled(Card)`
  margin-bottom: var(--space-xl);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-xl);
  margin-bottom: var(--space-xl);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: center;
  flex-wrap: wrap;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: var(--space-md);
`;

const Th = styled.th`
  text-align: left;
  padding: var(--space-md);
  border-bottom: 2px solid var(--gray-200);
  color: var(--gray-500);
`;

const Td = styled.td`
  padding: var(--space-md);
  border-bottom: 1px solid var(--gray-200);
`;

const SupplierHeader = styled.div`
  padding: var(--space-md);
  background: var(--primary-700);
  color: white;
  border-radius: var(--border-radius-md);
  margin-bottom: var(--space-sm);
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    margin: 0;
  }
`;

const PrevisioneAnnualePage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear() + 1);
    const [forecast, setForecast] = useState<any>(null);
    const [accuracy, setAccuracy] = useState<any>(null);
    const [puntoVenditaId, setPuntoVenditaId] = useState<number | null>(null);
    const [puntiVendita, setPuntiVendita] = useState<any[]>([]);
    const [, setExistingForecast] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
    const [useV2] = useState<boolean>(true); // Default: usa V2 con reasoning
    const [availableVersions, setAvailableVersions] = useState<any[]>([]);
    const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

    useEffect(() => {
        loadPuntiVendita();
    }, []);

    useEffect(() => {
        if (puntoVenditaId && year) {
            checkExistingForecast();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- verifica solo al cambio di punto vendita/anno; checkExistingForecast è ricreata ad ogni render
    }, [puntoVenditaId, year]);

    const loadPuntiVendita = async () => {
        try {
            const response = await ApiService.getPuntiVendita();
            setPuntiVendita(response);
            if (response.length > 0) {
                setPuntoVenditaId(response[0].id);
            }
        } catch (error) {
            console.error('Errore caricamento punti vendita:', error);
            setError('Errore nel caricamento dei punti vendita');
        }
    };

    const checkExistingForecast = async () => {
        try {
            const listResponse = await ApiService.getAnnualForecastList(year);
            const allVersions = listResponse.previsioni || [];

            // Filtra per punto vendita corrente
            const versions = allVersions.filter((p: any) => p.punto_vendita_id === puntoVenditaId);
            setAvailableVersions(versions);

            const exists = versions.length > 0;
            setExistingForecast(exists);

            // Se esiste, carica la versione più recente
            if (exists) {
                const latest = versions[0]; // Già ordinato per created_at DESC
                setSelectedVersionId(latest.id);
                await loadForecastDetails(latest.id);
            }
        } catch (error) {
            console.error('Errore verifica previsione esistente:', error);
            setExistingForecast(false);
        }
    };

    const generateForecast = async () => {
        if (!puntoVenditaId) {
            setError('Seleziona un punto vendita');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Genera SEMPRE una nuova versione (non controlla se esiste già)
            if (useV2) {
                // V2: Mostra modal e avvia streaming
                setShowProgressModal(true);
                setLoading(false);

                // Avvia generazione (la modal gestirà il progress)
                const response = await ApiService.generateAnnualForecastV2(puntoVenditaId, year);

                if (!response.success) {
                    setShowProgressModal(false);
                    throw new Error(response.error || 'Errore avvio generazione');
                }
            } else {
                // V1: Comportamento originale
                setSuccess('Generazione previsione in corso con AI (30-60 sec)...');
                const response = await ApiService.getAnnualForecast(puntoVenditaId, year);

                if (response.success) {
                    setForecast(response);
                    setSuccess('Previsione generata.');
                    setExistingForecast(true);

                    if (response.previsione_id) {
                        await loadAccuracy(response.previsione_id);
                    }
                } else {
                    throw new Error(response.error || 'Errore sconosciuto');
                }
                setLoading(false);
            }
        } catch (error: any) {
            console.error('Errore generazione previsione:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Errore durante la generazione della previsione';

            // Gestione specifica per storico insufficiente
            if (errorMsg.includes('Storico insufficiente')) {
                setError('Storico dati insufficiente per generare una previsione affidabile. Sono richiesti almeno 30 giorni di dati storici.');
            } else {
                setError(errorMsg);
            }

            // Mostra dettagli specifici
            if (error.response?.data?.details) {
                console.error('Dettagli errore:', error.response.data.details);
            }
            setLoading(false);
        }
    };

    const loadForecastDetails = async (id: number) => {
        try {
            const response = await ApiService.getAnnualForecastDetails(id);
            setForecast(response.previsione);
            await loadAccuracy(id);
        } catch (error: any) {
            console.error('Errore caricamento dettagli:', error);
            setError('Errore nel caricamento dei dettagli della previsione');
        }
    };

    const loadAccuracy = async (id: number) => {
        try {
            const response = await ApiService.getForecastAccuracy(id);
            setAccuracy(response);
        } catch (error: any) {
            console.error('Errore caricamento accuratezza:', error);
            // Non bloccare per questo errore
        }
    };

    const handleForecastComplete = async (previsioneId: number) => {
        console.log('Previsione completata. ID:', previsioneId);
        setShowProgressModal(false);
        setSuccess('Previsione generata.');

        // Ricarica lista versioni e seleziona la nuova
        await checkExistingForecast();
        setSelectedVersionId(previsioneId);
        await loadForecastDetails(previsioneId);
        setExistingForecast(true);
    };

    const handleVersionChange = async (versionId: number) => {
        setSelectedVersionId(versionId);
        await loadForecastDetails(versionId);
    };

    // Prepara dati per grafici
    const monthlyData = forecast?.dettagli
        ?.filter((d: any) => d.mese > 0 && !d.gusto_id && !d.fornitore && !d.ingrediente_id)
        .map((d: any) => ({
            name: new Date(2000, d.mese - 1, 1).toLocaleString('it-IT', { month: 'short' }),
            kg: parseFloat(d.kg_previsti)
        })) || [];

    // Prepara dati fornitori con ingredienti raggruppati
    const supplierDataRaw = forecast?.dettagli?.filter((d: any) => d.fornitore && !d.ingrediente_id && d.mese === 0) || [];
    const ingredientData = forecast?.dettagli?.filter((d: any) => d.ingrediente_id && d.mese === 0) || [];

    const supplierData = supplierDataRaw.map((supplier: any) => {
        const ingredients = ingredientData.filter((ing: any) => {
            // Match ingredients to supplier by fornitore field
            return ing.fornitore === supplier.fornitore;
        });

        return {
            name: supplier.fornitore,
            costo: parseFloat(supplier.costo_previsto),
            ingredienti: ingredients
        };
    });

    return (
        <>
        <ForecastProgressModal
            isOpen={showProgressModal}
            onClose={() => setShowProgressModal(false)}
            onComplete={handleForecastComplete}
        />
        <PageContainer>
            <PageHeader
                title="Previsione Annuale"
                icon={<TrendingUp />}
                actions={
                    <HeaderControls>
                        <Select
                            value={puntoVenditaId || ''}
                            onChange={(e) => setPuntoVenditaId(Number(e.target.value))}
                        >
                            {puntiVendita.map((pv: any) => (
                                <option key={pv.id} value={pv.id}>{pv.nome}</option>
                            ))}
                        </Select>
                        <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                            <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                            <option value={new Date().getFullYear() + 1}>{new Date().getFullYear() + 1}</option>
                            <option value={new Date().getFullYear() + 2}>{new Date().getFullYear() + 2}</option>
                        </Select>
                        {availableVersions.length > 0 && (
                            <Select
                                value={selectedVersionId || ''}
                                onChange={(e) => handleVersionChange(Number(e.target.value))}
                            >
                                {availableVersions.map((v: any, idx: number) => (
                                    <option key={v.id} value={v.id}>
                                        Versione {availableVersions.length - idx} - {new Date(v.created_at).toLocaleDateString('it-IT', {
                                            day: '2-digit',
                                            month: 'short',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </option>
                                ))}
                            </Select>
                        )}
                        <Button $variant="primary" onClick={generateForecast} disabled={loading || !puntoVenditaId}>
                            <Sparkles size={16} />
                            {loading
                                ? 'Elaborazione in corso...'
                                : 'Genera Nuova Versione'}
                        </Button>
                    </HeaderControls>
                }
            />

            {error && (
                <Alert $variant="error">
                    {error}
                </Alert>
            )}

            {success && (
                <Alert $variant="success">
                    {success}
                </Alert>
            )}

            {forecast && (
                <>
                    <StatGrid>
                        <StatCard
                            icon={<TrendingUp />}
                            value={`${parseFloat(forecast.totale_kg_previsto).toLocaleString()} kg`}
                            label={`Produzione annuale prevista ${year}`}
                        />
                        <StatCard
                            icon={<DollarSign />}
                            value={`€ ${supplierData.reduce((sum: number, d: any) => sum + d.costo, 0).toLocaleString()}`}
                            label="Budget fornitori - stima costi materie prime"
                        />
                        <StatCard
                            icon={<Target />}
                            value={accuracy?.accuratezza_media ? `${accuracy.accuratezza_media}%` : 'N/A'}
                            label={`Accuratezza media (${accuracy?.mesi_verificati || 0} mesi verificati)`}
                        />
                    </StatGrid>

                    <Grid>
                        <Card>
                            <CardHeader>
                                <h3>Trend Mensile Previsto</h3>
                            </CardHeader>
                            <CardBody>
                                <Alert $variant="info">
                                    <strong>Produzione mensile aggregata:</strong> Mostra i kg totali previsti per ogni mese dell'anno {year}.
                                    Gennaio è sempre a zero (chiusura). I valori sono calcolati applicando il fattore di crescita storico e
                                    la stagionalità rilevata negli anni precedenti.
                                </Alert>
                                <div style={{ height: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={monthlyData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="kg" fill="#7d8202" name="Kg Previsti" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader>
                                <h3>Top Gusti (Crescita)</h3>
                            </CardHeader>
                            <CardBody>
                                <Alert $variant="info">
                                    <strong>Distribuzione per gusto:</strong> Mostra i primi 5 gusti con i kg totali previsti per l'intero anno.
                                    I valori rappresentano la somma di tutti i mesi e sono basati sulla distribuzione del mix gusti
                                    rilevata nel 2025 applicata alla previsione {year}.
                                </Alert>
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Gusto</Th>
                                            <Th>Kg Previsti</Th>
                                            <Th>Trend</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Qui dovremmo filtrare i dati per gusto, per ora mostriamo placeholder o dati reali se disponibili */}
                                        {forecast.dettagli
                                            .filter((d: any) => d.gusto_id)
                                            .slice(0, 5)
                                            .map((d: any) => (
                                                <tr key={d.id}>
                                                    <Td>{d.gusto_nome || `Gusto #${d.gusto_id}`}</Td>
                                                    <Td>{parseFloat(d.kg_previsti).toFixed(1)} kg</Td>
                                                    <Td><Badge $variant="success">+15%</Badge></Td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Grid>

                    <SectionCard>
                        <CardHeader>
                            <h3>Breakdown Fornitori e Ingredienti</h3>
                        </CardHeader>
                        <CardBody>
                            <Alert $variant="info">
                                <strong>Fabbisogno materie prime annuale:</strong> Mostra il costo totale stimato per fornitore e il dettaglio degli ingredienti necessari.
                                Le quantità sono calcolate moltiplicando le ricette di ogni gusto per i kg previsti nell'intero anno {year}.
                                I costi sono basati sui prezzi unitari attuali degli ingredienti nel database.
                            </Alert>
                            {supplierData.map((supplier: any, i: number) => (
                                <div key={i} style={{ marginBottom: '2rem' }}>
                                    <SupplierHeader>
                                        <h4>{supplier.name}</h4>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                            € {supplier.costo.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </SupplierHeader>

                                    {supplier.ingredienti && supplier.ingredienti.length > 0 && (
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <Th>Ingrediente</Th>
                                                    <Th>Quantità Prevista</Th>
                                                    <Th>Costo Stimato</Th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {supplier.ingredienti
                                                    .map((ing: any, idx: number) => (
                                                        <tr key={idx}>
                                                            <Td>{ing.ingrediente_nome || `Ingrediente #${ing.ingrediente_id}`}</Td>
                                                            <Td>{parseFloat(ing.quantita_prevista || 0).toFixed(2)} {ing.unita || 'kg'}</Td>
                                                            <Td>€ {parseFloat(ing.costo_previsto || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </div>
                            ))}
                        </CardBody>
                    </SectionCard>
                </>
            )}
        </PageContainer>
        </>
    );
};

export default PrevisioneAnnualePage;
